<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

// State
const faqs = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const originalData = ref(null);
const modalType = ref('error');
const errorModalContent = ref([]);
let answerEditor = null;

// Delete state
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);
const deleting = ref(false);

const form = ref({
    id: null,
    faq_question: '',
    faq_answer: '',
    faq_icon: 'fa fa-question-circle',
    faq_active: true
});

// Methods
const loadFaqs = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/faq/get');
        if (response.data.status === 'success') {
            faqs.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading FAQs:', error);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = {
        id: null,
        faq_question: '',
        faq_answer: '',
        faq_icon: 'fa fa-question-circle',
        faq_active: true
    };

    isEditing.value = false;
    originalData.value = null;
    if (answerEditor) answerEditor.setData('');
};

// Normalize HTML for comparison
const normalizeHtml = (html) => {
    if (!html) return '';
    return html.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    nextTick(() => {
        try {
            const modalEl = document.getElementById('notificationModal');
            if (modalEl && typeof bootstrap !== 'undefined') {
                const modal = new bootstrap.Modal(modalEl);
                modal.show();
            } else {
                alert(Array.isArray(messages) ? messages.join('\n') : messages);
            }
        } catch (e) {
            alert(Array.isArray(messages) ? messages.join('\n') : messages);
        }
    });
};

const isDataChanged = () => {
    if (!originalData.value) return true;
    
    // Get current CKEditor content
    const currentAnswer = answerEditor ? answerEditor.getData() : form.value.faq_answer;
    
    // Compare only the fields that matter
    const currentQuestion = (form.value.faq_question || '').trim();
    const currentAnswerNorm = normalizeHtml(currentAnswer);
    const currentIcon = (form.value.faq_icon || 'fa fa-question-circle').trim();
    
    const origQuestion = (originalData.value.faq_question || '').trim();
    const origAnswerNorm = normalizeHtml(originalData.value.faq_answer || '');
    const origIcon = (originalData.value.faq_icon || 'fa fa-question-circle').trim();
    
    // Return true if ANY field is different
    const questionChanged = currentQuestion !== origQuestion;
    const answerChanged = currentAnswerNorm !== origAnswerNorm;
    const iconChanged = currentIcon !== origIcon;
    
    return questionChanged || answerChanged || iconChanged;
};

const editFaq = async (faq) => {
    form.value = {
        id: faq.id,
        faq_question: faq.faq_question,
        faq_answer: faq.faq_answer,
        faq_icon: faq.faq_icon || 'fa fa-question-circle',
        faq_active: faq.faq_active
    };
    
    originalData.value = { ...form.value }; // Store original data
    
    isEditing.value = true;
    if (answerEditor) {
        answerEditor.setData(faq.faq_answer || '');
    }
};

const submitForm = async () => {
    saving.value = true;
    
    // Small delay to show spinner effect (like Feature Page)
    await new Promise(resolve => setTimeout(resolve, 300));

    let url = '/admin/faq';
    // Sync CKEditor
    if (answerEditor) form.value.faq_answer = answerEditor.getData();
    
    // Client-side Validation
    const errors = [];
    if (!form.value.faq_question || form.value.faq_question.trim() === '') {
        errors.push('Pertanyaan FAQ wajib diisi.');
    }
    if (!form.value.faq_answer || form.value.faq_answer.trim() === '') {
        errors.push('Jawaban FAQ wajib diisi.');
    }
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    // Check for changes in edit mode
    if (isEditing.value && !isDataChanged()) {
        saving.value = false;
        window.__FAQ_VALIDATION_V2 = true;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    const data = { ...form.value };
    
    if (isEditing.value) {
        data._method = 'PUT';
        url = `/admin/faq/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, data);
        if (response.data.status === 'success') {
            resetForm();
            loadFaqs();
            // Success is silent (like Feature page)
        } else if (response.data.status === 'warning') {
            showModal('warning', response.data.message);
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            showModal('error', error.response.data.messages);
        } else {
            showModal('error', 'Terjadi kesalahan sistem');
        }
    } finally {
        saving.value = false;
    }
};

const togglingId = ref(null);

const toggleStatus = async (faq) => {
    if (togglingId.value) return; // Prevent double-click
    togglingId.value = faq.id;
    try {
        const response = await axios.patch(`/admin/faq/${faq.id}/toggle-status`);
        if (response.data.status === 'success') {
            // Update local state directly to avoid full reload flicker
            faq.faq_active = response.data.data.faq_active;
        }
    } catch (error) {
        const errorMessage = error.response?.data?.message || 'Gagal mengubah status';
        showModal('error', errorMessage);
    } finally {
        togglingId.value = null;
    }
};

// Delete Methods
const confirmDelete = (faq) => {
    deleteId.value = faq.id;
    deleteName.value = faq.faq_question;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value || !deleteConfirmedCheck.value) return;
    
    deleting.value = true;
    try {
        const response = await axios.delete(`/admin/faq/${deleteId.value}`);
        if (response.data.status === 'success') {
            // Close modal
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
            
            // Reload FAQs
            loadFaqs();
        } else {
            showModal('error', response.data.message || 'Gagal menghapus FAQ.');
        }
    } catch (error) {
        showModal('error', error.response?.data?.message || 'Gagal menghapus FAQ.');
    } finally {
        deleting.value = false;
    }
};

const initCKEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    // Destroy existing instances
    if (CKEDITOR.instances['faq_answer']) CKEDITOR.instances['faq_answer'].destroy();

    // Initialize editor
    answerEditor = CKEDITOR.replace('faq_answer', {
        height: 200,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'styles', items: ['Format'] }
        ],
        removePlugins: 'elementspath',
        resize_enabled: false
    });
};

onMounted(() => {
    loadFaqs();
    nextTick(() => {
        initCKEditor();
    });
});

onBeforeUnmount(() => {
    if (answerEditor) {
        answerEditor.destroy();
        answerEditor = null;
    }
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances['faq_answer']) {
        CKEDITOR.instances['faq_answer'].destroy();
    }
});
</script>

<template>
    <Head title="Kelola FAQ" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola FAQ</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">FAQ</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-5 col-12">
                    <div class="card">
                        <form @submit.prevent="submitForm">
                            <div class="card-header">
                                <h4 class="card-title">
                                    <i class="ti-help-alt me-5"></i>{{ isEditing ? 'Edit' : 'Tambah' }} FAQ
                                </h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group mb-3">
                                    <label for="faq_question" class="form-label">Pertanyaan <span class="text-danger">*</span></label>
                                    <textarea id="faq_question" v-model="form.faq_question" class="form-control" rows="3"></textarea>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="faq_answer" class="form-label">Jawaban <span class="text-danger">*</span></label>
                                    <textarea id="faq_answer" v-model="form.faq_answer" class="form-control" rows="5"></textarea>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="faq_icon" class="form-label">Icon Class</label>
                                    <input id="faq_icon" name="faq_icon" type="text" v-model="form.faq_icon" class="form-control" placeholder="fa fa-question-circle">
                                </div>
                                

                            </div>
                            <div class="box-footer text-end">
                                <button type="button" v-if="isEditing" @click="resetForm" class="btn btn-warning me-2"><i class="ti-trash me-1"></i> Batal</button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ti-save-alt me-1"></i> Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Table Panel -->
                <div class="col-xl-7 col-12">
                    <div class="card">
                        <div class="card-header">
                            <h4 class="card-title"><i class="ti-view-list me-10"></i>Daftar FAQ</h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center p-5">
                                <div class="spinner-border text-primary"></div>
                            </div>
                            <div v-else-if="faqs.length === 0" class="text-center text-muted p-5">
                                Belum ada FAQ
                            </div>
                            <div v-if="loading" class="text-center p-5">
                                <div class="spinner-border text-primary"></div>
                            </div>
                            <div v-else-if="faqs.length === 0" class="text-center text-muted p-5">
                                Belum ada FAQ
                            </div>
                            <div v-else class="accordion" id="faqAccordion">
                                <div v-for="(faq, index) in faqs" :key="faq.id" class="accordion-item">
                                    <h2 class="accordion-header">
                                        <button class="accordion-button collapsed" type="button" 
                                            :data-bs-target="'#faq-' + faq.id" data-bs-toggle="collapse">
                                            <i :class="[faq.faq_icon || 'fa fa-question-circle', 'me-2']"></i>
                                            {{ faq.faq_question }}
                                            <span v-if="!faq.faq_active" class="badge bg-danger ms-2">Nonaktif</span>
                                            <span v-else class="badge bg-success ms-2">Aktif</span>
                                        </button>
                                    </h2>
                                    <div :id="'faq-' + faq.id" class="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div class="accordion-body">
                                            <div v-html="faq.faq_answer" class="mb-3"></div>
                                            <div class="d-flex justify-content-end gap-2">
                                                 <button 
                                                    @click="toggleStatus(faq)" 
                                                    class="btn btn-sm rounded-1 px-3" 
                                                    :class="faq.faq_active ? 'btn-success' : 'btn-danger'"
                                                    :disabled="togglingId === faq.id"
                                                    title="Klik untuk mengubah status">
                                                    <span v-if="togglingId === faq.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else :class="['fa me-1', faq.faq_active ? 'fa-check-circle' : 'fa-times-circle']"></i>
                                                    {{ faq.faq_active ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                                
                                                <button class="btn btn-outline-primary btn-sm rounded-1" @click="editFaq(faq)">
                                                    <i class="fa fa-edit me-1"></i> Edit
                                                </button>
                                                
                                                <button class="btn btn-outline-danger btn-sm rounded-1" @click="confirmDelete(faq)">
                                                    <i class="fa fa-trash me-1"></i> Hapus
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Notification Modal -->
            <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header text-white" 
                            :class="{
                                'bg-danger': modalType === 'error',
                                'bg-warning text-dark': modalType === 'warning',
                                'bg-success': modalType === 'success',
                                'bg-info': modalType === 'info'
                            }">
                            <h5 class="modal-title">
                                <i class="me-2" :class="{
                                    'ti-alert': modalType === 'error',
                                    'fa fa-exclamation-triangle': modalType === 'warning',
                                    'fa fa-check-circle': modalType === 'success',
                                    'fa fa-info-circle': modalType === 'info'
                                }"></i>
                                {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                            </h5>
                            <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" 
                                    :class="{
                                        'list-group-item-danger': modalType === 'error',
                                        'list-group-item-warning': modalType === 'warning',
                                        'list-group-item-success': modalType === 'success',
                                        'list-group-item-info': modalType === 'info'
                                    }" 
                                    v-for="(msg, index) in errorModalContent" :key="index">
                                    <i class="fa me-2" :class="{
                                        'fa-exclamation-circle': modalType === 'error',
                                        'fa-exclamation-triangle': modalType === 'warning',
                                        'fa-check-circle': modalType === 'success',
                                        'fa-info-circle': modalType === 'info'
                                    }"></i>{{ msg }}
                                </li>
                            </ul>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn" 
                                :class="{
                                    'btn-danger': modalType === 'error',
                                    'btn-warning': modalType === 'warning',
                                    'btn-success': modalType === 'success',
                                    'btn-info': modalType === 'info'
                                }" 
                                data-bs-dismiss="modal">
                                <i class="fa fa-check-square-o me-2"></i>Mengerti
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Delete Confirmation Modal -->
            <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header bg-danger text-white">
                            <h5 class="modal-title" id="deleteModalLabel"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p>Apakah Anda yakin ingin menghapus FAQ:</p>
                            <p class="fw-bold text-primary">"{{ deleteName }}"</p>
                            <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                            
                            <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                                <input class="form-check-input" type="checkbox" id="confirmDeleteCheck" v-model="deleteConfirmedCheck">
                                <label class="form-check-label text-dark" for="confirmDeleteCheck">
                                    Saya mengerti dan yakin ingin menghapus data ini.
                                </label>
                            </div>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                            <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck || deleting" @click="submitDelete">
                                <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
                                <i v-else class="fa fa-trash me-2"></i>Ya, Hapus
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
